import { absolutePosition } from "./auto-fuse";
import { getDefinition } from "./catalog";
import { effectiveDimensions } from "./effective";
import type { ComponentNode, System, SystemNode } from "./types";

/**
 * Where the landing hero points its camera. Computed from system data, never
 * from React Flow's measured bounds — measurement order is unobservable here,
 * so reading it frames whichever corner was measured first.
 */
export interface HeroFrameRect {
  x: number;
  y: number;
  width: number;
  height: number;
}

export interface HeroFrameOptions {
  /** Pane size in screen px — the rect is fitted to this box. */
  paneWidth: number;
  paneHeight: number;
  minZoom?: number;
  maxZoom?: number;
  /** Breathing room around the focused nodes, in canvas units. */
  padding?: number;
}

export interface ExampleFrameOptions extends HeroFrameOptions {
  /** Screen pixels reserved below the drawing for overlaid controls. */
  bottomInset?: number;
}

/** Readability floor: 11 px cable labels (`cable-edge.tsx`) land at ~8 px. */
export const MIN_ZOOM = 0.7;
export const MAX_ZOOM = 0.95;

/** The authored homepage starts with the whole circuit in view, including when
 *  the result sidebar leaves less drawing space on a narrow laptop. */
export const HERO_MIN_ZOOM = 0.22;
export const PHONE_MAX_ZOOM = 0.65;
export const PHONE_PANE_WIDTH = 520;

export function heroZoomBand(paneWidth: number): {
  minZoom: number;
  maxZoom: number;
} {
  return {
    minZoom: HERO_MIN_ZOOM,
    maxZoom: paneWidth < PHONE_PANE_WIDTH ? PHONE_MAX_ZOOM : MAX_ZOOM,
  };
}
const PADDING = 56;
/** Clearance between a frame edge and the card it settles against. */
const EDGE_MARGIN = 24;
/** Must stay larger than any real frame's pill count, so a frame holding no
 *  whole component is disqualified rather than merely outscored. */
const EMPTY_PENALTY = 1000;
/** Half a cable label pill, in canvas units. */
const PILL_MARGIN = 44;

interface Rect extends HeroFrameRect {
  id: string;
}

interface Point {
  x: number;
  y: number;
}

function isDistributionPart(node: ComponentNode): boolean {
  const def = getDefinition(node.definitionId, node.customDefinition);
  if (!def) return false;
  return (
    def.isFuseBlock === true ||
    def.renderVariant === "busbar" ||
    def.renderVariant === "fuse-holder"
  );
}

function rectOf(node: SystemNode, nodes: SystemNode[]): Rect | null {
  const { x, y } = absolutePosition(node, nodes);
  if (node.kind === "zone") {
    return { id: node.id, x, y, width: node.width, height: node.height };
  }
  const def = getDefinition(node.definitionId, node.customDefinition);
  if (!def) return null;
  return { id: node.id, x, y, ...effectiveDimensions(def, node) };
}

function centreOf(rect: HeroFrameRect): Point {
  return { x: rect.x + rect.width / 2, y: rect.y + rect.height / 2 };
}

function union(rects: HeroFrameRect[]): HeroFrameRect | null {
  if (rects.length === 0) return null;
  const x = Math.min(...rects.map((r) => r.x));
  const y = Math.min(...rects.map((r) => r.y));
  const x2 = Math.max(...rects.map((r) => r.x + r.width));
  const y2 = Math.max(...rects.map((r) => r.y + r.height));
  return { x, y, width: x2 - x, height: y2 - y };
}

/** Resource examples can be much larger than the homepage. Fit the entire
 * drawing, including zones and cable detours, rather than cropping a subsystem. */
export function planExampleFrame(system: System, options: ExampleFrameOptions): HeroFrameRect | null {
  const { paneWidth, paneHeight, maxZoom = MAX_ZOOM, padding = PADDING, bottomInset = 0 } = options;
  if (paneWidth <= 0 || paneHeight <= 0) return null;
  // Keep at least half a tiny pane available to the drawing. The inset is in
  // screen pixels, so its clearance stays constant at any fitted zoom.
  const contentHeight = paneHeight - Math.max(0, Math.min(bottomInset, paneHeight / 2));
  const rects = system.nodes.flatMap((node) => {
    const rect = rectOf(node, system.nodes);
    return rect ? [rect] : [];
  });
  const bounds = union([
    ...rects,
    ...system.cables.flatMap((cable) => (cable.waypoints ?? []).map((point) => ({ ...point, width: 0, height: 0 }))),
  ]);
  if (!bounds) return null;
  const padded = toAspect({ x: bounds.x - padding, y: bounds.y - padding,
    width: bounds.width + padding * 2, height: bounds.height + padding * 2 }, paneWidth / contentHeight);
  const zoom = Math.min(paneWidth / padded.width, maxZoom);
  const frame = centredOn(centreOf(padded), paneWidth / zoom, contentHeight / zoom);
  // Fit to the full pane without recentering the content into the reserved strip.
  return { ...frame, height: paneHeight / zoom };
}

/** Grow the short axis about the centre until the rect matches `aspect`. */
function toAspect(rect: HeroFrameRect, aspect: number): HeroFrameRect {
  const current = rect.width / rect.height;
  if (current < aspect) {
    const width = rect.height * aspect;
    return { ...rect, x: rect.x - (width - rect.width) / 2, width };
  }
  const height = rect.width / aspect;
  return { ...rect, y: rect.y - (height - rect.height) / 2, height };
}

/** Shrink a rect by `by` on every side (floored at a degenerate point). */
function inset(rect: HeroFrameRect, by: number): HeroFrameRect {
  const width = Math.max(0, rect.width - by * 2);
  const height = Math.max(0, rect.height - by * 2);
  return {
    x: rect.x + (rect.width - width) / 2,
    y: rect.y + (rect.height - height) / 2,
    width,
    height,
  };
}

function contains(rect: HeroFrameRect, p: Point): boolean {
  return (
    p.x >= rect.x &&
    p.x <= rect.x + rect.width &&
    p.y >= rect.y &&
    p.y <= rect.y + rect.height
  );
}

function containsRect(outer: HeroFrameRect, inner: HeroFrameRect): boolean {
  return (
    inner.x >= outer.x &&
    inner.y >= outer.y &&
    inner.x + inner.width <= outer.x + outer.width &&
    inner.y + inner.height <= outer.y + outer.height
  );
}

function intersects(a: HeroFrameRect, b: HeroFrameRect): boolean {
  return (
    Math.min(a.x + a.width, b.x + b.width) > Math.max(a.x, b.x) &&
    Math.min(a.y + a.height, b.y + b.height) > Math.max(a.y, b.y)
  );
}

/** Clamp inside `bounds` on an axis where the frame is smaller, centre it
 *  where the frame is larger. */
function containedIn(rect: HeroFrameRect, bounds: HeroFrameRect): HeroFrameRect {
  const clampAxis = (
    pos: number,
    size: number,
    boundPos: number,
    boundSize: number,
  ) =>
    size >= boundSize
      ? boundPos + boundSize / 2 - size / 2
      : Math.min(Math.max(pos, boundPos), boundPos + boundSize - size);
  return {
    ...rect,
    x: clampAxis(rect.x, rect.width, bounds.x, bounds.width),
    y: clampAxis(rect.y, rect.height, bounds.y, bounds.height),
  };
}

function centredOn(centre: Point, width: number, height: number): HeroFrameRect {
  return { x: centre.x - width / 2, y: centre.y - height / 2, width, height };
}

/** Half-way along a polyline by arc length — where `cable-edge.tsx` puts the
 *  label pill. Duplicated (the engine can't import UI); keep the two in step. */
function arcMidpoint(points: Point[]): Point {
  const length = (i: number) =>
    Math.hypot(points[i + 1].x - points[i].x, points[i + 1].y - points[i].y);
  let total = 0;
  for (let i = 0; i < points.length - 1; i++) total += length(i);
  let target = total / 2;
  for (let i = 0; i < points.length - 1; i++) {
    const seg = length(i);
    if (target <= seg) {
      const t = seg === 0 ? 0 : target / seg;
      return {
        x: points[i].x + (points[i + 1].x - points[i].x) * t,
        y: points[i].y + (points[i + 1].y - points[i].y) * t,
      };
    }
    target -= seg;
  }
  return points[points.length - 1];
}

/** The canvas rect the hero should fit. Already at the pane's aspect ratio, so
 *  fit it with no padding. */
export function planHeroFrame(
  system: System,
  options: HeroFrameOptions,
): HeroFrameRect | null {
  const {
    paneWidth,
    paneHeight,
    minZoom = MIN_ZOOM,
    maxZoom = MAX_ZOOM,
    padding = PADDING,
  } = options;
  if (paneWidth <= 0 || paneHeight <= 0) return null;

  const nodes = system.nodes;
  const rects = new Map<string, Rect>();
  for (const node of nodes) {
    const rect = rectOf(node, nodes);
    if (rect) rects.set(node.id, rect);
  }
  const bounds = union([...rects.values()]);
  if (!bounds) return null;

  const components = nodes.filter(
    (n): n is ComponentNode => n.kind === "component",
  );
  const anchors = components.filter(isDistributionPart);
  const aimIds = (anchors.length > 0 ? anchors : components).map((n) => n.id);

  // Everything one cable away from an aim point, sized down by the zoom band.
  const aimSet = new Set(aimIds);
  const reachIds = new Set(aimSet);
  for (const cable of system.cables) {
    if (aimSet.has(cable.fromNodeId)) reachIds.add(cable.toNodeId);
    if (aimSet.has(cable.toNodeId)) reachIds.add(cable.fromNodeId);
  }
  const reachRects = [...reachIds]
    .map((id) => rects.get(id))
    .filter((r): r is Rect => r !== undefined);
  const reach = union(reachRects) ?? bounds;
  const wanted = toAspect(
    {
      x: reach.x - padding,
      y: reach.y - padding,
      width: reach.width + padding * 2,
      height: reach.height + padding * 2,
    },
    paneWidth / paneHeight,
  );
  const zoom = Math.min(Math.max(paneWidth / wanted.width, minZoom), maxZoom);
  const width = paneWidth / zoom;
  const height = paneHeight / zoom;

  const pills: Point[] = [];
  for (const cable of system.cables) {
    const from = rects.get(cable.fromNodeId);
    const to = rects.get(cable.toNodeId);
    if (!from || !to) continue;
    pills.push(
      arcMidpoint([centreOf(from), ...(cable.waypoints ?? []), centreOf(to)]),
    );
  }
  const componentRects = components
    .map((n) => rects.get(n.id))
    .filter((r): r is Rect => r !== undefined);

  // Most cable pills wins; whole components break ties.
  const score = (frame: HeroFrameRect) => {
    // A pill counts only clear of the edge; by centre, a sliced number scores.
    let total = pills.filter((p) => contains(inset(frame, PILL_MARGIN), p)).length;
    let whole = 0;
    for (const rect of componentRects) {
      if (containsRect(frame, rect)) {
        whole += 1;
        total += 0.5;
      } else if (intersects(frame, rect)) total -= 0.5;
    }
    return whole === 0 ? total - EMPTY_PENALTY : total;
  };

  const candidates = [
    ...aimIds.map((id) => rects.get(id)).filter((r): r is Rect => r !== undefined),
    reach,
  ].map((r) => centreOf(r));

  let best: HeroFrameRect | null = null;
  let bestScore = -Infinity;
  for (const candidate of candidates) {
    const frame = containedIn(centredOn(candidate, width, height), bounds);
    const value = score(frame);
    if (value > bestScore) {
      bestScore = value;
      best = frame;
    }
  }
  if (!best) return null;

  // Aim points are component centres, so the frame's edges usually land through
  // a row of cards; slide each axis onto nearby card edges instead.
  for (const axis of ["x", "y"] as const) {
    const size = axis === "x" ? best.width : best.height;
    const start = best[axis];
    const offsets = new Set<number>();
    for (const rect of componentRects) {
      const pos = axis === "x" ? rect.x : rect.y;
      const extent = axis === "x" ? rect.width : rect.height;
      offsets.add(pos - EDGE_MARGIN);
      offsets.add(pos + extent + EDGE_MARGIN - size);
    }
    for (const offset of [...offsets].sort((a, b) => a - b)) {
      if (Math.abs(offset - start) > size / 3) continue;
      const candidate = containedIn({ ...best, [axis]: offset }, bounds);
      const value = score(candidate);
      if (value > bestScore + 1e-9) {
        bestScore = value;
        best = candidate;
      }
    }
  }

  return best;
}
